import { deepClone } from '../helpers';
import { ApplicationBase } from './ApplicationBase';
import { PythonApplicationLoadSource } from './constants/PythonApplicationLoadSource';
import { createDefaultSourceFile } from './constants';
import { ApplicationTypes } from './constants/ApplicationTypes';
import utils from "../utils";


// TODO: The PythonApplication should not extend Application, but - instead - some
// sort of universal "BaseApplication": https://github.com/VentionCo/mm-programmatic-sdk/issues/16
// I am tentative to do this now, however, as it would be a major change in the code. I recommend
// that we tackle this once Guillaume's file refactor is in.

/**
 * @typedef {import('./constants/PythonApplicationDefaults').SourceFile} SourceFile
 */

/**
 * Saved JSON for the PythonApplication.
 *
 * @typedef {Object} PythonApplicationJson
 * @property {string} loadSource
 * @property {SourceFile} singleSourceFile
 */

/**
 * MachineLogic representation of a Python Application.
 */
export class PythonApplication extends ApplicationBase {
  constructor(pLibrary, documentationUrl) {
    super(pLibrary, ApplicationTypes.Python, false, true);
    this.mLoadSource = PythonApplicationLoadSource.SingleFile;
    this.mSingleSourceFile = createDefaultSourceFile(pLibrary, documentationUrl);
  }

  setDirty(pCustomTimeOut, pBeforeDeploy = null) {
    super.setDirty(pCustomTimeOut, () => {
      this.setVersionHash();
      pBeforeDeploy && pBeforeDeploy();
    });
  }

  getApplicationVersionHash() {
    const clone = this.toJson();
    delete clone.uuid;

    const hash = utils.generateHashFromString(JSON.stringify(clone));
    return hash;
  }

  /**
   * Create a version hash and use it as the mUUID
   */
  setVersionHash() {
    this.mUUID = this.getApplicationVersionHash().toString();
  }

  /**
   * If the loadSource is "SingleFile", this will return the single "file"
   * containing the user's Python code.
   *
   * @returns {SourceFile} Code contained in the default single file.
   */
  getSingleSourceFile() {
    if (this.mLoadSource !== PythonApplicationLoadSource.SingleFile) {
      throw new Error("Cannot retrieve mSingleFileCode when the PythonApplication was loaded from another source");
    }

    return { ...this.mSingleSourceFile };
  }

  /**
   * The load source of the project.
   * @returns {string} The source from which the project was loaded.
   */
  getLoadSourceType() {
    return this.mLoadSource;
  }

  /**
   * Changes the code in the provided file.
   * @param {string} filename The file that you want to change
   * @param {string} code That you want to alter the file with
   * @throws If the file does not exist
   */
  changeSourceFile(filename, code) {
    switch (this.mLoadSource) {
      case PythonApplicationLoadSource.SingleFile: {
        if (this.getSingleSourceFile().filename !== filename) {
          throw new Error(`Attempting to change a source file that does not exist: ${filename}. Valid options are: ${this.getSingleSourceFile().filename}.`);
        }
        this.mSingleSourceFile = { filename, code };
        this.setDirty();
        this.notify('onSourceFileChanged', this.getSingleSourceFile());
        break;
      }
      default: {
        throw new Error(`Unsupported load source type: ${this.mLoadSource}`);
      }
    }
  }

  /**
   * Returns the JSON of the PythonApplication.
   *
   * @returns {PythonApplicationJson}
   */
  toJson() {
    return {
      ...super.toJson(),
      loadSource: this.mLoadSource,
      singleSourceFile: deepClone(this.mSingleSourceFile),
      uuid: this.mUUID,
    };
  }

  /**
   * Populates the PythonApplication from the json object.
   * @param {PythonApplicationJson} pJson
   */
  static fromJson(pLibrary, pJson) {
    const lApp = new PythonApplication(pLibrary, "");
    ApplicationBase.fromJson(lApp, pJson);

    lApp.mLoadSource = pJson.loadSource;
    lApp.mSingleSourceFile = deepClone(pJson.singleSourceFile);

    const applicationUi = lApp.getUi();
    applicationUi.setIsReadonly(true);
    const tab = applicationUi.getTabByIndex(0);
    const consoleWidget = tab.addWidget('console');
    consoleWidget.setPosition(0, 0);
    consoleWidget.setDimensionByCellSize(8, 5);

    return lApp;
  }
}
